import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiX, FiRefreshCw, FiClock, FiAlertTriangle, FiCheckCircle, FiInfo,
  FiActivity, FiZap,
} from 'react-icons/fi';

interface ReparseAllDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /** Optional stats: how many reports total / with CSV. Shown as preview chips
   *  so the user knows roughly what to expect before clicking confirm. */
  totalReports?: number;
  reportsWithCsv?: number;
}

/** Live progress payload from SignalR ("BulkReparseProgress" event). */
interface BulkReparseProgress {
  current: number;
  total: number;
  transactionsSoFar: number;
  elapsedSeconds: number;
  etaSeconds?: number | null;
  currentReportId?: string | null;
  completed: boolean;
  lastReportFailed?: boolean;
  lastReportError?: string | null;
}

/**
 * Two-phase dialog:
 *   - Phase 1 ("confirm"): rich explanation of what the operation does, expected
 *     duration, limitations, and a confirm/cancel pair.
 *   - Phase 2 ("running"): live progress bar fed by SignalR's BulkReparseProgress
 *     event. Shows X / Y reports, transactions so far, elapsed, ETA, and the
 *     id of the report currently being processed.
 *
 * Phase switches automatically when `loading` flips to true. The popup stays
 * open during the whole operation -- the parent's POST request hangs until the
 * server finishes, but SignalR pushes intermediate progress over a separate
 * channel so the UI doesn't look frozen.
 *
 * Theme support: every color is either a CSS variable (--text, --border,
 * --bg-elevated etc.) or a hard-coded brand accent that works in both light
 * and dark mode (RGBA with low opacity backgrounds).
 */
const ReparseAllDialog: React.FC<ReparseAllDialogProps> = ({
  isOpen, onConfirm, onCancel, loading = false,
  totalReports, reportsWithCsv,
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<BulkReparseProgress | null>(null);

  // Subscribe to live progress from SignalR. The hook (useSignalR) re-broadcasts
  // hub events as DOM CustomEvents so dialogs/components don't need to import
  // the connection directly.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BulkReparseProgress>).detail;
      if (detail) setProgress(detail);
    };
    window.addEventListener('bulk-reparse-progress', handler);
    return () => window.removeEventListener('bulk-reparse-progress', handler);
  }, [isOpen]);

  // Reset progress only when the modal closes (not when loading flips back to
  // false). The SignalR Completed event and the POST response race each other
  // -- if Completed arrives first the user sees the success banner; if POST
  // returns first we'd otherwise clear the still-incoming completion state.
  // Tying the reset to `isOpen` instead means the success banner persists until
  // the user dismisses the dialog, then state cleans up for the next open.
  useEffect(() => {
    if (!isOpen) setProgress(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const reportsWithoutCsv =
    typeof totalReports === 'number' && typeof reportsWithCsv === 'number'
      ? Math.max(0, totalReports - reportsWithCsv)
      : undefined;

  return (
    <div
      className="modal-overlay"
      onClick={loading ? undefined : onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reparse-all-title"
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '600px' }}
      >
        {/* --- Header --------------------------------------------------- */}
        <div className="modal-header" style={{ alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: loading ? 'rgba(59, 130, 246, 0.12)' : 'rgba(249, 115, 22, 0.12)',
              color: loading ? '#3b82f6' : '#f97316',
              fontSize: '20px',
              flexShrink: 0,
            }}>
              {loading
                ? <FiActivity style={{ animation: 'spin 1.6s linear infinite' }} />
                : <FiRefreshCw />}
            </div>
            <div>
              <h2 id="reparse-all-title" style={{ margin: 0 }}>
                {t('settlements.reparseAllModalTitle')}
              </h2>
              {loading && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {t('settlements.reparseAllRunning')}
                </div>
              )}
            </div>
          </div>
          <button
            className="btn btn-icon"
            onClick={onCancel}
            disabled={loading}
            aria-label={t('common.close')}
          >
            <FiX />
          </button>
        </div>

        {/* --- Body ----------------------------------------------------- */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {loading
            ? <RunningView progress={progress} totalReports={totalReports} t={t} />
            : <ConfirmView
                totalReports={totalReports}
                reportsWithCsv={reportsWithCsv}
                reportsWithoutCsv={reportsWithoutCsv}
                t={t}
              />
          }
        </div>

        {/* --- Footer --------------------------------------------------- */}
        {/* Three states for the footer:
            * loading=false, no progress  -> "Cancel" + "Yes, recalculate" (initial)
            * loading=true, no completion -> just "Cancel" disabled + spinning button
            * loading=false, completion arrived -> single "Close" button (success state)
            We rely on the parent NOT auto-closing on success; the SignalR Completed
            event flips progress.completed to true and we render the success footer. */}
        <div className="modal-footer">
          {progress?.completed ? (
            <button
              type="button"
              className="btn"
              onClick={onCancel}
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
                color: '#fff',
                fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}
            >
              <FiCheckCircle />
              {t('common.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={onConfirm}
                disabled={loading}
                style={{
                  backgroundColor: loading ? '#3b82f6' : '#f97316',
                  borderColor: loading ? '#3b82f6' : '#f97316',
                  color: '#fff',
                  fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  opacity: loading ? 0.85 : 1,
                }}
              >
                <FiRefreshCw style={{ animation: loading ? 'spin 1s linear infinite' : undefined }} />
                {loading ? t('settlements.reparseAllRunning') : t('settlements.reparseAllConfirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================ */
/*  Confirm view -- shown before the user clicks "Yes"           */
/* ============================================================ */

interface ConfirmViewProps {
  totalReports?: number;
  reportsWithCsv?: number;
  reportsWithoutCsv?: number;
  t: any;
}

const ConfirmView: React.FC<ConfirmViewProps> = ({ totalReports, reportsWithCsv, reportsWithoutCsv, t }) => (
  <>
    <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: 'var(--text)' }}>
      {t('settlements.reparseAllModalIntro')}
    </p>

    {typeof totalReports === 'number' && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '8px',
      }}>
        <PreviewChip
          label={t('settlements.statTotalReports')}
          value={totalReports}
          color="var(--text)"
          bg="var(--bg-secondary, var(--bg-elevated))"
        />
        {typeof reportsWithCsv === 'number' && (
          <PreviewChip
            label={t('settlements.csvAvailable')}
            value={reportsWithCsv}
            color="#10b981"
            bg="rgba(16, 185, 129, 0.12)"
          />
        )}
        {typeof reportsWithoutCsv === 'number' && reportsWithoutCsv > 0 && (
          <PreviewChip
            label={t('settlements.csvMissing')}
            value={reportsWithoutCsv}
            color="#ef4444"
            bg="rgba(239, 68, 68, 0.12)"
          />
        )}
      </div>
    )}

    <Callout color="#10b981" icon={<FiCheckCircle />} title={t('settlements.reparseAllModalWhatItDoes')}>
      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.7, color: 'var(--text)' }}>
        <li>{t('settlements.reparseAllModalStep1')}</li>
        <li>{t('settlements.reparseAllModalStep2')}</li>
        <li>{t('settlements.reparseAllModalStep3')}</li>
      </ul>
    </Callout>

    <Callout color="#3b82f6" icon={<FiClock />} title={t('settlements.reparseAllModalDuration')}>
      <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--text)' }}>
        <strong>{t('settlements.reparseAllModalDurationValue')}</strong>{' '}
        {t('settlements.reparseAllModalDurationDetail')}
      </div>
    </Callout>

    <Callout color="#f59e0b" icon={<FiAlertTriangle />} title={t('settlements.reparseAllModalLimitation')}>
      <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--text)' }}>
        {t('settlements.reparseAllModalLimitationText')}
      </div>
    </Callout>
  </>
);

/* ============================================================ */
/*  Running view -- live progress while the bulk job runs        */
/* ============================================================ */

interface RunningViewProps {
  progress: BulkReparseProgress | null;
  totalReports?: number;
  t: any;
}

const RunningView: React.FC<RunningViewProps> = ({ progress, totalReports, t }) => {
  // We have no progress yet on the very first render after click. Use the
  // pre-flight stats (totalReports) as a reasonable initial estimate so the
  // bar isn't blank in those first ~200ms.
  const total = progress?.total ?? totalReports ?? 0;
  const current = progress?.current ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  const transactions = progress?.transactionsSoFar ?? 0;
  const elapsed = progress?.elapsedSeconds ?? 0;
  const eta = progress?.etaSeconds ?? null;
  const completed = progress?.completed ?? false;
  const failed = progress?.lastReportFailed ?? false;
  const failedMsg = progress?.lastReportError ?? null;

  const fmtDuration = useCallback((sec: number): string => {
    if (sec < 60) return `${Math.round(sec)}s`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec - m * 60);
    return `${m}m ${s}s`;
  }, []);

  const barColor = completed ? '#10b981' : (failed ? '#f59e0b' : '#3b82f6');

  return (
    <>
      {/* Top status line */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          {completed
            ? t('settlements.reparseAllRunningDone')
            : t('settlements.reparseAllRunningProgress', { current, total: total || '?' })}
        </div>
        <div style={{ fontSize: '24px', fontWeight: 700, color: barColor }}>
          {pct}%
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: '14px',
        borderRadius: '7px',
        backgroundColor: 'var(--bg-secondary, rgba(107, 114, 128, 0.15))',
        border: '1px solid var(--border)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: completed
            ? 'linear-gradient(90deg, #10b981, #34d399)'
            : 'linear-gradient(90deg, #3b82f6, #60a5fa)',
          transition: 'width 200ms ease-out, background 300ms ease',
          borderRadius: '6px',
          // Subtle animated stripe overlay while running -- pure CSS, no extra DOM.
          backgroundSize: '40px 40px',
          ...(completed ? {} : {
            backgroundImage:
              'linear-gradient(90deg, #3b82f6, #60a5fa), ' +
              'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%)',
            backgroundBlendMode: 'overlay',
          }),
        }} />
      </div>

      {/* Stats grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '8px',
        marginTop: '4px',
      }}>
        <ProgressStat
          icon={<FiCheckCircle />}
          label={t('settlements.reparseAllStatReports')}
          value={`${current.toLocaleString('pl-PL')} / ${total.toLocaleString('pl-PL')}`}
          color="#3b82f6"
        />
        <ProgressStat
          icon={<FiZap />}
          label={t('settlements.reparseAllStatTransactions')}
          value={transactions.toLocaleString('pl-PL')}
          color="#10b981"
        />
        <ProgressStat
          icon={<FiClock />}
          label={t('settlements.reparseAllStatElapsed')}
          value={fmtDuration(elapsed)}
          color="var(--text)"
        />
        {!completed && eta != null && (
          <ProgressStat
            icon={<FiClock />}
            label={t('settlements.reparseAllStatEta')}
            value={`~${fmtDuration(eta)}`}
            color="var(--text-muted)"
          />
        )}
      </div>

      {/* Currently-processing line */}
      {!completed && progress?.currentReportId && (
        <div style={{
          fontSize: '12px',
          color: 'var(--text-muted)',
          padding: '8px 12px',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-secondary, rgba(59, 130, 246, 0.05))',
          border: '1px solid var(--border)',
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        }}>
          <span style={{ color: 'var(--text)' }}>
            {t('settlements.reparseAllStatCurrent')}:
          </span>{' '}
          {progress.currentReportId}
        </div>
      )}

      {/* Failure flag for last report (loop continues, but flag it). */}
      {failed && (
        <div style={{
          display: 'flex', gap: '8px', alignItems: 'flex-start',
          padding: '10px 12px', borderRadius: '8px',
          backgroundColor: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          fontSize: '12px', color: 'var(--text)',
        }}>
          <FiAlertTriangle style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong style={{ color: '#f59e0b' }}>
              {t('settlements.reparseAllLastFailed')}:
            </strong>{' '}
            {failedMsg || t('settlements.reparseAllError')}
          </div>
        </div>
      )}

      {/* Completion banner */}
      {completed && (
        <div style={{
          display: 'flex', gap: '10px', alignItems: 'center',
          padding: '12px 14px', borderRadius: '10px',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          fontSize: '13px', color: 'var(--text)',
        }}>
          <FiCheckCircle style={{ color: '#10b981', fontSize: '18px', flexShrink: 0 }} />
          <div>
            <strong style={{ color: '#10b981' }}>
              {t('settlements.reparseAllRunningDone')}
            </strong>{' '}
            -{' '}
            {t('settlements.reparseAllSuccess', {
              reports: current,
              transactions,
            })}
          </div>
        </div>
      )}
    </>
  );
};

/* ============================================================ */
/*  Small reusable bits                                          */
/* ============================================================ */

interface ProgressStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

const ProgressStat: React.FC<ProgressStatProps> = ({ icon, label, value, color }) => (
  <div style={{
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-secondary, var(--bg-elevated))',
    border: '1px solid var(--border)',
    minWidth: 0,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      marginBottom: '4px',
    }}>
      <span style={{ color, fontSize: '12px', display: 'inline-flex' }}>{icon}</span>
      {label}
    </div>
    <div style={{ fontSize: '15px', fontWeight: 700, color, lineHeight: 1.2 }}>
      {value}
    </div>
  </div>
);

interface CalloutProps {
  color: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Callout: React.FC<CalloutProps> = ({ color, icon, title, children }) => (
  <div style={{
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '10px',
    // Compose RGBA from the brand color via CSS color-mix-ish trick: low-opacity
    // backgrounds work in both light + dark themes without theme-specific overrides.
    backgroundColor: `${color}14`,    // ~8% alpha
    border: `1px solid ${color}40`,   // ~25% alpha
  }}>
    <div style={{ color, fontSize: '20px', flexShrink: 0, marginTop: '2px', display: 'inline-flex' }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '13px', fontWeight: 700, color,
        marginBottom: '6px',
        textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        {title}
      </div>
      {children}
    </div>
  </div>
);

interface PreviewChipProps {
  label: string;
  value: number;
  color: string;
  bg: string;
}

const PreviewChip: React.FC<PreviewChipProps> = ({ label, value, color, bg }) => (
  <div style={{
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: bg,
    border: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column', gap: '2px',
    minWidth: 0,
  }}>
    <div style={{
      fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 700, color, lineHeight: 1.2 }}>
      {value.toLocaleString('pl-PL')}
    </div>
  </div>
);

void FiInfo;

export default ReparseAllDialog;
